// Hand-drawn 32×32 studio furniture. All shapes stay inside their collision tile;
// the caller owns the floor, lighting and animation layers.
const DEFAULT_PALETTE = {
  ink: '#264653',
  cream: '#f5f0e1',
  creamDark: '#e8dcc4',
  creamShadow: '#d8cbae',
  wood: '#b08968',
  woodDark: '#7f5539',
  woodLight: '#c89f7d',
  wall: '#2a4a56',
  wallDark: '#1b323b',
  mint: '#a8dadc',
  mintDark: '#8bc4c2',
  coral: '#f4a261',
  gold: '#e9c46a',
};

/**
 * Paint a single solid prop using the engine's palette.
 * Supported kinds: production, storyboard, script, outline, assets, music,
 * cost and gallery. Other kinds receive a quiet writing desk.
 */
export function drawStudioProp(g, x, y, kind, palette = DEFAULT_PALETTE) {
  const c = { ...DEFAULT_PALETTE, ...palette };
  const originX = Math.round(x);
  const originY = Math.round(y);
  const rect = (color, px, py, width, height) => {
    g.fillStyle = color;
    g.fillRect(originX + px, originY + py, width, height);
  };

  // A two-pixel stepped contact shadow, never a whole-tile backdrop.
  const shadow = () => {
    rect(c.creamShadow, 5, 28, 25, 2);
    rect(c.creamShadow, 28, 23, 2, 5);
  };

  const desk = () => {
    shadow();
    rect(c.woodDark, 3, 16, 26, 10);
    rect(c.woodDark, 4, 25, 3, 4);
    rect(c.woodDark, 25, 25, 3, 4);
    rect(c.woodLight, 2, 14, 28, 9);
    rect(c.creamDark, 3, 14, 26, 1);
    rect(c.wood, 3, 22, 26, 2);
    rect(c.woodDark, 2, 24, 28, 2);
    rect(c.wood, 4, 26, 2, 2);
    rect(c.wood, 25, 26, 2, 2);
  };

  const paper = (px, py, width = 8, height = 9) => {
    rect(c.creamDark, px + 1, py + 1, width, height);
    rect(c.cream, px, py, width, height);
    rect(c.creamDark, px + width - 2, py, 2, 2);
    rect(c.wood, px + 2, py + 3, width - 4, 1);
    rect(c.wood, px + 2, py + 5, width - 5, 1);
  };

  const cup = (px, py) => {
    rect(c.ink, px, py + 1, 5, 5);
    rect(c.cream, px, py, 4, 5);
    rect(c.woodDark, px + 1, py + 1, 2, 1);
    rect(c.cream, px + 4, py + 1, 2, 3);
    rect(c.woodLight, px + 4, py + 2, 1, 1);
  };

  const easel = () => {
    shadow();
    rect(c.woodDark, 6, 21, 3, 6);
    rect(c.woodDark, 4, 27, 5, 3);
    rect(c.woodDark, 23, 21, 3, 6);
    rect(c.woodDark, 23, 27, 5, 3);
    rect(c.wood, 8, 25, 16, 2);
  };

  switch (kind) {
    case 'production': {
      desk();
      // Desktop monitor with distinct preview, timeline tracks and playhead.
      rect(c.ink, 4, 2, 18, 13);
      rect(c.wallDark, 5, 3, 16, 10);
      rect(c.creamDark, 6, 4, 7, 4);
      rect(c.mintDark, 7, 6, 5, 2);
      rect(c.creamDark, 15, 4, 4, 1);
      rect(c.wood, 15, 6, 3, 1);
      rect(c.mint, 6, 9, 5, 1);
      rect(c.mint, 12, 9, 7, 1);
      rect(c.woodLight, 6, 11, 9, 1);
      rect(c.woodLight, 16, 11, 3, 1);
      rect(c.coral, 13, 8, 1, 5);
      rect(c.ink, 11, 15, 4, 2);
      rect(c.ink, 8, 17, 10, 1);
      rect(c.creamDark, 7, 19, 12, 3);
      rect(c.wood, 8, 20, 8, 1);
      rect(c.cream, 20, 19, 2, 3);
      rect(c.gold, 24, 9, 5, 4);
      rect(c.woodDark, 25, 10, 3, 1);
      cup(23, 15);
      break;
    }
    case 'storyboard': {
      easel();
      rect(c.woodDark, 2, 2, 28, 21);
      rect(c.woodLight, 3, 3, 26, 19);
      rect(c.cream, 4, 4, 24, 16);
      // Three actual frames: establishing shot, medium shot, close-up.
      [5, 13, 21].forEach((px) => {
        rect(c.ink, px, 6, 6, 10);
        rect(c.creamDark, px + 1, 7, 4, 8);
        rect(c.wood, px, 18, 5, 1);
      });
      rect(c.mintDark, 6, 12, 4, 3);
      rect(c.ink, 7, 10, 1, 3);
      rect(c.ink, 9, 11, 1, 2);
      rect(c.woodDark, 15, 9, 2, 2);
      rect(c.mintDark, 14, 11, 4, 4);
      rect(c.woodDark, 22, 8, 4, 2);
      rect(c.woodLight, 22, 10, 4, 4);
      rect(c.ink, 23, 11, 1, 1);
      rect(c.cream, 11, 21, 10, 1);
      break;
    }
    case 'script': {
      desk();
      // A low bookshelf behind an open manuscript.
      rect(c.woodDark, 3, 2, 26, 12);
      rect(c.woodLight, 4, 3, 24, 10);
      rect(c.creamDark, 5, 5, 3, 7);
      rect(c.mintDark, 9, 4, 3, 8);
      rect(c.cream, 13, 6, 3, 6);
      rect(c.woodDark, 17, 5, 3, 7);
      rect(c.creamDark, 21, 4, 5, 8);
      rect(c.wood, 22, 6, 3, 1);
      rect(c.cream, 10, 5, 1, 2);
      rect(c.woodDark, 3, 12, 26, 2);
      paper(9, 14, 9, 9);
      rect(c.cream, 19, 16, 6, 6);
      rect(c.wood, 20, 18, 3, 1);
      rect(c.ink, 6, 17, 1, 6);
      rect(c.gold, 6, 17, 1, 1);
      break;
    }
    case 'outline': {
      desk();
      // Connected story beats, not an unlabelled generic monitor.
      rect(c.woodDark, 3, 2, 26, 13);
      rect(c.creamDark, 4, 3, 24, 11);
      rect(c.wood, 10, 6, 4, 1);
      rect(c.wood, 18, 6, 4, 1);
      rect(c.wood, 15, 7, 1, 5);
      rect(c.wood, 9, 11, 13, 1);
      rect(c.cream, 6, 4, 5, 4);
      rect(c.mint, 13, 4, 5, 4);
      rect(c.cream, 21, 4, 5, 4);
      rect(c.cream, 7, 10, 5, 3);
      rect(c.gold, 20, 10, 5, 3);
      rect(c.woodDark, 7, 5, 3, 1);
      rect(c.ink, 14, 5, 3, 1);
      rect(c.woodDark, 22, 5, 3, 1);
      paper(9, 16, 10, 7);
      rect(c.ink, 22, 17, 1, 6);
      break;
    }
    case 'assets': {
      shadow();
      // Labelled material drawers on the left, a visible garment rail on the right.
      rect(c.woodDark, 2, 5, 14, 24);
      rect(c.woodLight, 3, 6, 12, 21);
      [8, 14, 20].forEach((py) => {
        rect(c.wood, 4, py, 10, 5);
        rect(c.creamDark, 7, py + 1, 4, 2);
        rect(c.woodDark, 8, py + 3, 2, 1);
      });
      rect(c.creamDark, 4, 3, 10, 2);
      rect(c.ink, 18, 4, 12, 2);
      rect(c.ink, 18, 6, 2, 23);
      rect(c.ink, 29, 6, 1, 23);
      rect(c.ink, 17, 28, 14, 2);
      rect(c.woodDark, 24, 6, 1, 3);
      rect(c.woodDark, 22, 9, 5, 1);
      rect(c.mintDark, 21, 11, 8, 4);
      rect(c.mint, 23, 10, 4, 14);
      rect(c.mintDark, 23, 23, 4, 2);
      rect(c.cream, 24, 10, 2, 2);
      rect(c.creamDark, 24, 15, 1, 5);
      break;
    }
    case 'music': {
      shadow();
      // Two speaker cabinets and a centre console with independent faders.
      [2, 23].forEach((px) => {
        rect(c.ink, px, 5, 7, 23);
        rect(c.wallDark, px + 1, 6, 5, 21);
        rect(c.creamDark, px + 2, 8, 3, 3);
        rect(c.wood, px + 2, 17, 3, 1);
        rect(c.wood, px + 1, 18, 5, 4);
        rect(c.wood, px + 2, 22, 3, 1);
        rect(c.ink, px + 2, 19, 3, 2);
      });
      rect(c.ink, 10, 5, 12, 7);
      rect(c.mintDark, 11, 6, 10, 5);
      rect(c.cream, 12, 8, 3, 1);
      rect(c.cream, 16, 7, 1, 3);
      rect(c.cream, 18, 8, 2, 1);
      rect(c.ink, 9, 13, 14, 16);
      rect(c.wood, 10, 14, 12, 13);
      [11, 14, 17, 20].forEach((px, index) => {
        rect(c.ink, px, 16, 1, 2);
        rect(c.woodDark, px, 19, 1, 6);
        rect(c.cream, px, 20 + (index % 3), 2, 2);
      });
      rect(c.coral, 10, 27, 2, 1);
      break;
    }
    case 'cost': {
      desk();
      // Open ledger alongside an abacus: rails remain continuous through the beads.
      rect(c.woodDark, 3, 5, 12, 10);
      rect(c.cream, 4, 6, 10, 8);
      rect(c.wood, 8, 6, 1, 8);
      rect(c.wood, 5, 8, 2, 1);
      rect(c.wood, 10, 8, 3, 1);
      rect(c.wood, 5, 11, 2, 1);
      rect(c.wood, 10, 11, 3, 1);
      rect(c.woodDark, 17, 3, 13, 12);
      rect(c.creamDark, 18, 4, 11, 10);
      [20, 23, 26].forEach((px, index) => {
        rect(c.wood, px, 4, 1, 10);
        rect(c.woodDark, px - 1, 5, 3, 2);
        rect(c.woodDark, px - 1, 9 + (index % 2) * 2, 3, 2);
      });
      rect(c.woodDark, 18, 8, 11, 1);
      paper(7, 17, 10, 6);
      rect(c.ink, 22, 17, 6, 7);
      rect(c.mint, 23, 18, 4, 2);
      rect(c.cream, 23, 21, 1, 1);
      rect(c.cream, 25, 21, 1, 1);
      rect(c.cream, 23, 23, 1, 1);
      rect(c.cream, 25, 23, 1, 1);
      break;
    }
    case 'gallery': {
      easel();
      rect(c.woodDark, 3, 2, 26, 22);
      rect(c.woodLight, 4, 3, 24, 20);
      rect(c.cream, 5, 4, 22, 18);
      rect(c.creamDark, 7, 6, 18, 14);
      rect(c.gold, 19, 8, 3, 3);
      rect(c.mintDark, 8, 15, 5, 4);
      rect(c.mintDark, 10, 13, 3, 3);
      rect(c.mintDark, 12, 11, 3, 8);
      rect(c.mintDark, 15, 14, 3, 5);
      rect(c.wood, 15, 17, 9, 2);
      rect(c.wood, 18, 15, 5, 3);
      rect(c.cream, 13, 21, 6, 1);
      break;
    }
    default: {
      desk();
      paper(8, 11, 11, 11);
      rect(c.ink, 20, 17, 1, 6);
      cup(23, 15);
    }
  }
}

/** One continuous reception counter for a confirmed 2×2 block of R tiles. */
export function drawReceptionCounter(g, x, y, palette = DEFAULT_PALETTE) {
  const c = { ...DEFAULT_PALETTE, ...palette };
  const originX = Math.round(x);
  const originY = Math.round(y);
  const rect = (color, px, py, width, height) => {
    g.fillStyle = color;
    g.fillRect(originX + px, originY + py, width, height);
  };

  // Stepped contact shadow and one shared cabinet carcass.
  rect(c.creamShadow, 7, 60, 54, 2);
  rect(c.creamShadow, 60, 41, 2, 19);
  rect(c.woodDark, 4, 36, 56, 23);
  rect(c.woodDark, 7, 58, 50, 3);
  rect(c.wood, 5, 40, 54, 15);
  rect(c.woodDark, 6, 55, 52, 2);
  // Cabinet seams, not separate desks: the countertop and plinth stay unbroken.
  rect(c.woodLight, 6, 41, 15, 13);
  rect(c.woodLight, 23, 41, 17, 13);
  rect(c.woodLight, 42, 41, 16, 13);
  rect(c.woodDark, 20, 45, 1, 5);
  rect(c.woodDark, 42, 45, 1, 5);

  // Continuous broad tabletop with a restrained warm front edge.
  rect(c.woodDark, 2, 21, 60, 19);
  rect(c.woodLight, 3, 22, 58, 15);
  rect(c.creamDark, 4, 22, 56, 1);
  rect(c.wood, 3, 37, 58, 3);
  rect(c.coral, 4, 38, 56, 1);

  // Reception display and keyboard on the left corner.
  rect(c.ink, 8, 8, 18, 16);
  rect(c.wallDark, 9, 9, 16, 12);
  rect(c.mintDark, 10, 10, 14, 10);
  rect(c.cream, 11, 11, 12, 2);
  rect(c.creamDark, 11, 15, 3, 4);
  rect(c.cream, 16, 15, 6, 1);
  rect(c.cream, 16, 18, 4, 1);
  rect(c.ink, 15, 24, 4, 3);
  rect(c.ink, 12, 27, 10, 2);
  rect(c.creamDark, 10, 31, 15, 4);
  rect(c.wood, 11, 32, 10, 1);
  rect(c.wood, 13, 34, 7, 1);
  rect(c.cream, 27, 31, 3, 4);

  // Open visitor register with a visible centre binding and a pen.
  rect(c.woodDark, 40, 25, 15, 11);
  rect(c.creamDark, 41, 26, 13, 9);
  rect(c.cream, 41, 25, 6, 9);
  rect(c.cream, 48, 25, 6, 9);
  rect(c.wood, 42, 27, 4, 1);
  rect(c.wood, 49, 27, 4, 1);
  rect(c.wood, 42, 30, 3, 1);
  rect(c.wood, 49, 30, 4, 1);
  rect(c.ink, 57, 27, 1, 8);
  rect(c.gold, 57, 27, 1, 1);

  // Small upright welcome plaque, plus one inlaid counter badge.
  rect(c.woodDark, 31, 19, 7, 9);
  rect(c.cream, 32, 20, 5, 6);
  rect(c.coral, 33, 21, 3, 1);
  rect(c.wood, 33, 23, 3, 1);
  rect(c.woodDark, 30, 27, 9, 2);
  rect(c.woodDark, 26, 44, 11, 7);
  rect(c.creamDark, 27, 45, 9, 5);
  rect(c.coral, 28, 46, 2, 3);
  rect(c.woodDark, 31, 46, 4, 1);
  rect(c.woodDark, 31, 48, 3, 1);
}
